/**
 * Templates Library — installer view.
 *
 * Mounted at `/templates` (optionally `?brand=probound|proepoxy|prodecor`).
 * Backend `GET /api/templates` already filters to brands the installer has
 * `access[brand]` for, so this page just needs to render + filter what it
 * gets back. Actions: Open (file download) and Copy (body text to clipboard).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Home, FileText, Download, Copy, Filter, Sparkles } from 'lucide-react';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import PersonalizeTemplateModal from '@/components/PersonalizeTemplateModal';

const BRANDS = [
  { key: 'probound', label: 'ProBound', color: '#2F5E96' },
  { key: 'proepoxy', label: 'ProEpoxy', color: '#7A0B7A' },
  { key: 'prodecor', label: 'ProDecor', color: '#D4A24B' },
];

const CATEGORIES = [
  { key: 'quote_letter',   label: 'Quote Letters' },
  { key: 'rams',           label: 'RAMS' },
  { key: 'customer_email', label: 'Customer Email' },
  { key: 'tcs',            label: 'Terms & Conditions' },
  { key: 'warranty',       label: 'Warranty' },
  { key: 'aftercare',      label: 'Aftercare' },
];

export default function TemplatesLibrary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandParam = searchParams.get('brand');
  const isLocked = !!(brandParam && BRANDS.find((b) => b.key === brandParam));
  const lockedBrand = isLocked ? BRANDS.find((b) => b.key === brandParam) : null;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brandFilter, setBrandFilter] = useState(isLocked ? brandParam : '');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [personalizing, setPersonalizing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (brandFilter) qs.set('brand', brandFilter);
      if (categoryFilter) qs.set('category', categoryFilter);
      const url = `${API_URL}/api/templates${qs.toString() ? `?${qs.toString()}` : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load templates');
      setTemplates(await res.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const copyText = async (t) => {
    try {
      await navigator.clipboard.writeText(t.body_text);
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const headerBg = lockedBrand
    ? `linear-gradient(135deg,#0B0B0E 0%, ${lockedBrand.color} 75%)`
    : 'linear-gradient(135deg,#0B0B0E 0%, #1f4030 75%)';
  const accent = lockedBrand?.color || '#D4A24B';

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of templates) {
      const key = t.category;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return CATEGORIES
      .map((c) => ({ ...c, items: map.get(c.key) || [] }))
      .filter((c) => c.items.length > 0);
  }, [templates]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: headerBg, borderBottom: `3px solid ${accent}` }}>
        <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(-1)} data-testid="tpl-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate">
              <FileText className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: accent }} />
              {isLocked ? `${lockedBrand.label} — Templates` : 'Templates'}
            </h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">
              Quote letters, RAMS, customer emails, warranty &amp; aftercare docs — download or copy the text and use with your own customers.
            </p>
          </div>
          <Button onClick={() => navigate('/')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Home</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 py-5">
        {/* Brand filter — hidden when the page is locked to a brand via ?brand= */}
        {!isLocked && (
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <Filter className="w-4 h-4 text-gray-500" />
            <button data-testid="tpl-brand-filter-all" onClick={() => setBrandFilter('')} className={`text-xs px-3 py-1.5 rounded-full border-2 ${brandFilter === '' ? 'border-gray-900 bg-gray-900 text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}>All brands</button>
            {BRANDS.map((b) => (
              <button
                key={b.key}
                data-testid={`tpl-brand-filter-${b.key}`}
                onClick={() => setBrandFilter(b.key)}
                className={`text-xs px-3 py-1.5 rounded-full border-2 ${brandFilter === b.key ? 'text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}
                style={brandFilter === b.key ? { background: b.color, borderColor: b.color } : {}}
              >
                {b.label}
              </button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <button data-testid="tpl-cat-filter-all" onClick={() => setCategoryFilter('')} className={`text-xs px-3 py-1 rounded-full border ${categoryFilter === '' ? 'border-gray-900 bg-gray-900 text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}>All categories</button>
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`tpl-cat-filter-${c.key}`}
              onClick={() => setCategoryFilter(c.key)}
              className={`text-xs px-3 py-1 rounded-full border ${categoryFilter === c.key ? 'border-gray-900 bg-gray-900 text-white font-semibold' : 'border-gray-200 bg-white text-gray-600'}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : templates.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-1">No templates available</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">Nothing here yet for your Ox Approved sections. Check back soon.</p>
          </Card>
        ) : (
          <div className="space-y-6">
            {grouped.map((cat) => (
              <div key={cat.key}>
                <h2 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-2">{cat.label}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cat.items.map((t) => {
                    const b = BRANDS.find((x) => x.key === t.brand) || { label: t.brand, color: '#666' };
                    return (
                      <Card key={t.id} className="p-4 flex flex-col gap-2" data-testid={`tpl-card-${t.id}`}>
                        <span className="text-[10px] font-bold uppercase text-white px-2 py-0.5 rounded-full self-start" style={{ background: b.color }}>{b.label}</span>
                        <h3 className="font-semibold text-sm" title={t.title}>{t.title}</h3>
                        {t.description && <p className="text-xs text-gray-500 line-clamp-3">{t.description}</p>}
                        {t.body_text && (
                          <details className="text-xs text-gray-600 bg-gray-50 rounded-md px-2 py-1">
                            <summary className="cursor-pointer font-medium">Preview text</summary>
                            <pre className="whitespace-pre-wrap mt-1 font-sans text-[11px] leading-snug max-h-40 overflow-auto">{t.body_text}</pre>
                          </details>
                        )}
                        {t.file_name && (
                          <p className="text-[10px] text-gray-400 mt-auto">{t.file_name} · {(t.file_size / 1024).toFixed(0)} KB</p>
                        )}
                        <div className="flex gap-1.5 mt-1 flex-wrap">
                          {t.file_url && (
                            <a href={t.file_url} download target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[110px]">
                              <Button size="sm" variant="outline" className="w-full h-8 text-xs" data-testid={`tpl-download-${t.id}`}>
                                <Download className="w-3.5 h-3.5 mr-1" /> Open / Download
                              </Button>
                            </a>
                          )}
                          {t.body_text && (
                            <Button size="sm" variant="outline" className="flex-1 min-w-[110px] h-8 text-xs" onClick={() => copyText(t)} data-testid={`tpl-copy-${t.id}`}>
                              <Copy className="w-3.5 h-3.5 mr-1" /> Copy text
                            </Button>
                          )}
                          {t.body_text && (
                            <Button size="sm" className="flex-1 min-w-[110px] h-8 text-xs bg-gray-900 hover:bg-black text-white" onClick={() => setPersonalizing(t)} data-testid={`tpl-personalize-${t.id}`}>
                              <Sparkles className="w-3.5 h-3.5 mr-1" /> Personalize
                            </Button>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {personalizing && (
        <PersonalizeTemplateModal template={personalizing} onClose={() => setPersonalizing(null)} />
      )}
    </div>
  );
}
